import { useCallback, useEffect, useState } from 'react';

import { useStores } from '../../../stores/useStores';
import { sendResetPasswordStart } from '../loginAnalytics';
import type { useHandleAuth } from '../useHandleAuth';

type HandleAuth = ReturnType<typeof useHandleAuth>['handleAuth'];

/**
 * Force-reset state for a log-in form. The returned `handleAuth` records whether an email log-in hit a
 * force-reset account; the form clears it when the player edits a field. `startResetPassword` tracks the tap and
 * hands the email to the Reset Password screen when it came from the force-reset link ("Forgot password" opens it
 * empty). A form opened from the security email's link starts in that state, with `initialEmail` filled in.
 */
export function useForceReset(handleAuth: HandleAuth) {
  const { authStore } = useStores();
  const [initialEmail] = useState(() => authStore.forceResetLoginEmail ?? '');
  const [forceResetEmail, setForceResetEmail] = useState<string | null>(() => authStore.forceResetLoginEmail ?? null);

  // Read once: the next log-in form opens normally.
  useEffect(() => {
    authStore.forceResetLoginEmail = null;
  }, [authStore]);

  const handleAuthWithForceReset = useCallback<HandleAuth>(
    async (params) => {
      const result = await handleAuth(params);
      if (params.provider === 'email' && params.action === 'log_in') {
        setForceResetEmail(result.forceReset ? (params.credentials?.email ?? '').trim() : null);
      }
      return result;
    },
    [handleAuth],
  );

  const clearForceReset = useCallback(() => setForceResetEmail(null), []);

  const startResetPassword = (openResetPassword: () => void) => {
    sendResetPasswordStart(forceResetEmail === null ? 'forgot_password' : 'force_reset_error');
    authStore.passwordResetPrefillEmail = forceResetEmail;
    openResetPassword();
  };

  return {
    initialEmail,
    handleAuth: handleAuthWithForceReset,
    isForceReset: forceResetEmail !== null,
    clearForceReset,
    startResetPassword,
  };
}

/** The email a Reset Password screen starts with; cleared when the screen closes so the next one opens empty. */
export function usePasswordResetPrefillEmail() {
  const { authStore } = useStores();
  useEffect(
    () => () => {
      authStore.passwordResetPrefillEmail = null;
    },
    [authStore],
  );
  return authStore.passwordResetPrefillEmail ?? '';
}
